<template>
    <div>
        <div class="columns toolbar-box action-right">
            <div class="column is-3">
                <h1 class="title is-3 between-center">
                    <span>
                        <span>Production Report</span>
                        <a href="#" @click.prevent="isTableSettingsModalActive = true" class="gear">
                            <o-icon pack="fa" icon="gear" size="is-small"/>
                        </a>
                    </span>
                </h1>
            </div>

            <div class="column is-4-tablet is-2-desktop">
                <o-field>
                    <o-select v-model="filters.team_id" placeholder="Select Team" expanded>
                        <option value="">All teams</option>
                        <option v-for="team in teams" :value="team.id" :key="team.id">
                           {{ team.name }}
                        </option>
                    </o-select>
                </o-field>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <datepicker v-model="filters.from"
                                input-class="input is-fullwidth"
                                placeholder="Week ending from" :absolute="true"
                    />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <datepicker v-model="filters.to"
                            input-class="input is-fullwidth"
                            placeholder="Week ending to" :absolute="true"
                />
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <o-checkbox v-model="filters.allDates">Include empty dates</o-checkbox>
                </div>
            </div>
        </div>
        <div class="columns toolbar-box action-right">
            <div class="column is-4-tablet is-2-desktop is-offset-3">
                <div class="field">
                    <datepicker v-model="filters.converted_from"
                                input-class="input is-fullwidth"
                                placeholder="Converted from" :absolute="true"
                    />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <datepicker v-model="filters.converted_to"
                            input-class="input is-fullwidth"
                            placeholder="Converted to" :absolute="true"
                />
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete field-name="name" v-model="projects" type="project" placeholder="Projects" />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <o-checkbox v-model="viewAll">View All</o-checkbox>
                </div>
            </div>
        </div>
        <div class="columns">
            <div class="column is-1-desktop">&nbsp;</div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete field-name="title" v-model="exclude_services" type="service" placeholder="Exclude Services" />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete field-name="title" v-model="services" type="service" placeholder="Services" />
                </div>
            </div>

            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete field-name="full_name" v-model="users" type="user" placeholder="Project manager" />
                </div>
            </div>
            <div class="column is-4-tablet is-2-desktop">
                <div class="field">
                    <multicomplete field-name="full_name" v-model="serviceUsers" type="user" placeholder="Assigned To" />
                </div>
            </div>

            <div class="column is-3 action-box">
                <div class="field is-grouped is-grouped-right">
                    <p v-if="isResetFiltersButtonVisible" class="control">
                        <a @click.prevent="resetFilters" class="button is-info"><span>Reset filters</span></a>
                    </p>
                    <p class="control">
                        <a :href="`/api/reports/production-report/csv?api_token=${apiToken}&${queryString}`" class="button is-primary"><span>Download CSV</span></a>
                    </p>
                </div>
            </div>
        </div>
        <br>
        <o-table
                class="fullwidth-table is-responsive is-responsive-desktop"

                v-if="isTableVisible"
                :data="paginatedData"

                :bordered="table.bordered"
                :striped="table.striped"
                :narrowed="table.narrowed"
                :checkable="false"
                :mobile-cards="table.mobileCards"
                :paginated="table.paginated"
                :pagination-simple="table.paginationSimple"
                :per-page="table.perPage"
                :default-sort="table.defaultSort"
                root-class="overflow"

                :selected.sync="selected"
                :checked-rows="checkedRows"
                @update:checkedRows="$event => checkedRows = $event"

                :loading="loading"
                :total="total"
                backend-sorting
                backend-pagination
                @page-change="onPageChange"
                @sort="onSort"
        >
            <template>
                <template>
                    <div v-for="col in tableColumns">
                      <o-table-column
                          :field="col.field"
                          :label="col.field === 'actions' ? '' : col.label"
                          :key="col.field"
                          :sortable="col.sortable"
                          #default="{ row }"
                      >
                        <span v-if="col.field === 'project_borough'">{{ $filters.valueOrDefault(boroughNameById(row[col.field]), '-') }}</span>
                        <span v-else-if="col.field === 'team'">{{ $filters.valueOrDefault(teamNameById(row.team_id), '-') }}</span>
                        <span v-else-if="col.field === 'work_types'">{{ $filters.valueOrDefault(workTypeNamesByIds(row[col.field]), '-') }}</span>
                        <span v-else-if="col.field === 'reimbursable'">{{ row.reimbursable ? 'Y' : 'N' }}</span>
                        <span v-else-if="col.field === 'week_ending'">{{  $filters.valueOrDefault($filters.date(row.week_ending), '-') }}</span>
                        <span v-else-if="col.field === 'notes_created_at'">{{ $filters.valueOrDefault($filters.date(row[col.field]), '-') }}</span>
                        <span v-else-if="col.field === 'notes'">{{ $filters.valueOrDefault(row[col.field], '-') }}</span>
                        <span v-else-if="col.field === 'service_price'">{{ $filters.valueOrDefault($filters.currency(row[col.field]), '-') }}</span>
                        <span v-else-if="col.field === 'billed_price'">{{ $filters.valueOrDefault($filters.currency(row[col.field]), '-') }}</span>

                        <span v-else-if="col.field === 'project_number'">
                            <router-link :to="`/projects/${row[col.field]}/services`">{{ $filters.valueOrDefault(row[col.field], '-') }}</router-link>
                        </span>

                        <span v-else>
                            {{ $filters.valueOrDefault(row[col.field], '-') }}
                        </span>
                      </o-table-column>
                    </div>
                </template>
            </template>

          <template #empty>
            <div class="has-text-centered">
              This table is empty!
            </div>
          </template>
        </o-table>

        <div v-if="totalExpenses">
          Service $: <strong>{{ $filters.currency(totalExpenses) }}</strong><br>
          Count: <strong>{{ total }}</strong> rows
        </div>

       <o-modal :can-cancel="['escape', 'outside', 'button']" scroll="keep" v-model:active="isTableSettingsModalActive" has-modal-card class="table-settings-modal">
            <table-settings-modal :slug="tableName"/>
        </o-modal>
    </div>
</template>

<script>
    import ProductionReportModal from './../../../components/modals/ProductionReport'
    import Datepicker from './../../../components/Datepicker'
    import Table from './../../../mixins/Table'
    import { mapGetters } from 'vuex'
    import qs from 'query-string'

    // this code is so extreme
    export default {
        mixins: [Table],

        components: {
            Datepicker,
            ProductionReportModal,
        },

        data() {
            return {
                viewAll: false,
                totalExpenses: null,
                urlFilter: { enabled: false },
                tableName: 'production-report',
                isTableSettingsModalActive: false,

                table: {
                    perPage: 12,
                    defaultSort: ['proposal_date', 'desc']
                },

                filters: {
                    from: null,
                    to: null,
                    converted_from: null,
                    converted_to: null,
                    team_id: null,
                    users: [],
                    serviceUsers: [],
                    services: [],
                    projects: [],
                    exclude_services: [],
                    allDates: false
                },

                users: [],
                serviceUsers: [],
                services: [],
                projects: [],
                exclude_services: [],
                items: []
            }
        },

        mounted() {
            this.fetchPaginatedData()
        },

        computed: {
            ...mapGetters([
                'workTypeNamesByIds',
                'boroughNameById',
                'teamNameById',
                'teams'
            ]),

            queryString() {
                return qs.stringify(this.filters, {arrayFormat: 'bracket'})
            },

            apiToken() {
                return Laravel.currentUser.api_token
            }
        },

        watch: {
            filters: {
                deep: true,
                handler(value) {
                  console.log(value)
                    this.fetchPaginatedData()
                }
            },

            viewAll: {
                immediate: true,
                handler() {
                    this.table.paginated = ! this.viewAll

                    this.fetchPaginatedData()
                }
            },

            'filters.users'(v) {
              if (v.length === 0 && this.users.length) this.users = []
            },
            'filters.serviceUsers'(v) {
              if (v.length === 0 && this.serviceUsers.length) this.serviceUsers = []
            },
            'filters.services'(v) {
              if (v.length === 0 && this.services.length) this.services = []
            },
            'filters.projects'(v) {
              if (v.length === 0 && this.projects.length) this.projects = []
            },
            'filters.exclude_services'(v) {
              if (v.length === 0 && this.exclude_services.length) this.exclude_services = []
            },

            'users'(value) {
              // this.filters.users = _.map(this.users, 'id')
              this.filters.users = value
            },
            'serviceUsers'(value) {
              // this.filters.serviceUsers = _.map(this.serviceUsers, 'id')
              this.filters.serviceUsers = value
            },
            'services'(value) {
              // this.filters.services = _.map(this.services, 'id')
              this.filters.services = value
            },
            'exclude_services'(value) {
              // this.filters.exclude_services = _.map(this.exclude_services, 'id')
              this.filters.exclude_services = value
            },
            'projects'(value) {
              // this.filters.projects = _.map(this.projects, 'id')
              this.filters.projects = value
            }
        },

        methods: {
            fetchData(params) {
                return axios.get(`/api/reports/production-report`, { params })
                    .then(res => {
                        this.items = res.data.data
                        this.totalExpenses = res.data.totalExpenses
                        return res
                    })
            },

            itemsByIds() {
                return this.items
            }
        }
    }
</script>